import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import EnterpriseAddNotaryModal from "../modals/EnterpriseAddNotaryModal";

const EnterpriseNotariesGrid = ({ currentSession, currentUser }) => {
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removeLoadingEmail, setRemoveLoadingEmail] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [addNotaryErrorText, setAddNotaryErrorText] = useState("");
  const [foundNotary, setFoundNotary] = useState(null);
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);

  const fetchNotaries = async () => {
    setFetching(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/notaries/list`,
        { currentSession },
        { withCredentials: true },
      );

      setRows(resp?.data?.data || []);
    } catch (error) {
      setErrorText(
        error?.response?.data?.message || "Failed to load enterprise notaries.",
      );
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchNotaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  useEffect(() => {
    if (!errorText && !successText) return;
    const timer = setTimeout(() => {
      setErrorText("");
      setSuccessText("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [errorText, successText]);

  const formik = useFormik({
    initialValues: {
      first: "",
      last: "",
      email: "",
      commission_number: "",
    },
    validationSchema: Yup.object({
      first: Yup.string().min(2).required("First name is required"),
      last: Yup.string().min(2).required("Last name is required"),
      email: Yup.string()
        .email("Must be a valid email")
        .required("Email is required"),
      commission_number: Yup.string().required("Commission number is required"),
    }),
    onSubmit: async (values, helpers) => {
      setSubmitting(true);
      setErrorText("");
      setSuccessText("");
      setAddNotaryErrorText("");

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/search-notary-by-name`,
          {
            first_name: values.first,
            last_name: values.last,
            commission_number: values.commission_number,
            currentSession,
          },
          { withCredentials: true },
        );

        if (resp?.data?.success) {
          setFoundNotary(resp?.data?.notary || null);
          setAddNotaryErrorText("");
          setEnterpriseModalOpen(true);
        } else {
          setFoundNotary(null);
          setAddNotaryErrorText(
            resp?.data?.message ||
              "Notary not found with the provided name and commission number.",
          );
        }
      } catch (error) {
        setAddNotaryErrorText(
          error?.response?.data?.message ||
            "Notary not found with the provided name and commission number.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleInviteSuccess = (responseData) => {
    setRows((prev) => [responseData?.data, ...prev]);
    setSuccessText(responseData?.message || "Invitation sent successfully.");
    setIsExpanded(false);
    setAddNotaryErrorText("");
    setFoundNotary(null);
    formik.resetForm();
  };

  const handleRemove = async (email) => {
    setRemoveLoadingEmail(email);
    setErrorText("");
    setSuccessText("");
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/notaries/delete`,
        { email, currentSession },
        { withCredentials: true },
      );

      setRows((prev) => prev.filter((row) => row?.email !== email));
      setSuccessText(resp?.data?.message || "Notary removed successfully.");
    } catch (error) {
      setErrorText(
        error?.response?.data?.message || "Failed to remove notary.",
      );
    } finally {
      setRemoveLoadingEmail("");
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "full_name",
        headerName: "Name",
        flex: 1.3,
        minWidth: 180,
        valueGetter: (_, row) =>
          row?.full_name ||
          `${row?.first_name || ""} ${row?.last_name || ""}`.trim(),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.4,
        minWidth: 220,
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 0.9,
        minWidth: 130,
        valueGetter: (_, row) => row?.phone || "N/A",
      },
      {
        field: "commission_number",
        headerName: "Commission #",
        flex: 0.9,
        minWidth: 150,
        valueGetter: (_, row) => row?.commission_number || "N/A",
      },
      {
        field: "commission_expiry",
        headerName: "Commission Expiry",
        flex: 0.9,
        minWidth: 150,
        valueGetter: (_, row) => row?.commission_expiry || "N/A",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.9,
        minWidth: 140,
        renderCell: (params) => {
          const isRegistered = Boolean(params?.row?.on_site_registered);
          return (
            <Chip
              size="small"
              color={isRegistered ? "success" : "warning"}
              label={isRegistered ? "Active" : "Invitation Sent"}
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_, row) =>
          row?.created_at
            ? new Date(row.created_at * 1000).toLocaleDateString()
            : "N/A",
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        width: 80,
        renderCell: (params) => (
          <Tooltip title="Remove Notary">
            <span>
              <IconButton
                color="error"
                disabled={removeLoadingEmail === params?.row?.email}
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemove(params?.row?.email);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [removeLoadingEmail],
  );

  return (
    <Card sx={{ width: "100%", borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" color="primary">
              Enterprise Notaries
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage the organization notary pool for this enterprise
              account.
            </Typography>
          </Box>

          <Tooltip title="Refresh Notaries">
            <span>
              <IconButton
                onClick={fetchNotaries}
                disabled={fetching}
                color="primary"
              >
                <RefreshIcon className={fetching ? "rotate" : ""} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {!!errorText && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorText}
          </Alert>
        )}
        {!!successText && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successText}
          </Alert>
        )}

        <Box sx={{}}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row?.email || row?.notary_id}
            autoHeight
            disableRowSelectionOnClick
            disableColumnMenu
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
            }}
            sx={{
              border: "1px solid #FCF2F0",
              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none !important",
              },
            }}
          />
        </Box>

        <Accordion expanded={isExpanded} elevation={0} sx={{ mt: 3 }}>
          <AccordionSummary component="div" sx={{ px: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              {isExpanded ? (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RemoveIcon />}
                  onClick={() => {
                    setIsExpanded(false);
                    setErrorText("");
                    setAddNotaryErrorText("");
                    setFoundNotary(null);
                    formik.resetForm();
                  }}
                >
                  Hide
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsExpanded(true)}
                >
                  Add Notary
                </Button>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" align="center">
                    Add Enterprise Notary
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first"
                    value={formik.values.first}
                    onChange={(e) => {
                      setAddNotaryErrorText("");
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.first && Boolean(formik.errors.first)}
                    helperText={formik.touched.first && formik.errors.first}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last"
                    value={formik.values.last}
                    onChange={(e) => {
                      setAddNotaryErrorText("");
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.last && Boolean(formik.errors.last)}
                    helperText={formik.touched.last && formik.errors.last}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={(e) => {
                      setAddNotaryErrorText("");
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Commission Number"
                    name="commission_number"
                    value={formik.values.commission_number}
                    onChange={(e) => {
                      setAddNotaryErrorText("");
                      formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.commission_number &&
                      Boolean(formik.errors.commission_number)
                    }
                    helperText={
                      formik.touched.commission_number &&
                      formik.errors.commission_number
                    }
                  />
                </Grid>
                {!!addNotaryErrorText && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="error">{addNotaryErrorText}</Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={submitting}
                    fullWidth
                  >
                    Proceed
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </AccordionDetails>
        </Accordion>

        <EnterpriseAddNotaryModal
          open={enterpriseModalOpen}
          onClose={() => setEnterpriseModalOpen(false)}
          currentSession={currentSession}
          draftValues={formik.values}
          foundNotary={foundNotary}
          onSuccess={handleInviteSuccess}
        />
      </CardContent>
    </Card>
  );
};

export default EnterpriseNotariesGrid;
